import { useCallback, useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { Alert } from "antd";
import { GetAuditoriaClinicaCitas } from "../../Service/Service.auditoriaClinica";
import AuditoriaClinicaFiltros, { AUDITORIA_FILTROS_EMPTY } from "../../Components/AuditoriaClinica/AuditoriaClinicaFiltros";
import AuditoriaClinicaTabla from "../../Components/AuditoriaClinica/AuditoriaClinicaTabla";
import AuditoriaClinicaDetalleDrawer from "../../Components/AuditoriaClinica/AuditoriaClinicaDetalleDrawer";

// AUDITORIA-CLINICA-FASE-1 -- página `/auditoria-clinica` (ver context/37_
// AUDITORIA_CLINICA_FLUJO_GLOBAL_35.md §33/§34/§42). 100% lectura: la única
// acción de fila es "Ver auditoría" (abre el drawer), nunca "Editar".
function buildFilters(draft) {
    const [d0, d1] = draft.rangoFechas || [];
    return {
        fecha_desde: d0 ? d0.format("YYYY-MM-DD") : undefined,
        fecha_hasta: d1 ? d1.format("YYYY-MM-DD") : undefined,
        paciente: draft.paciente || undefined,
        documento: draft.documento || undefined,
        doctor: draft.doctor,
        estado_cita: draft.estado_cita,
        cobertura: draft.cobertura,
        aseguradora: draft.aseguradora,
        plan: draft.plan,
        corregida: draft.corregida !== undefined ? draft.corregida === "true" : undefined,
    };
}

// Filtros por defecto (hoy: estado de cita = Finalizada). Se calculan una sola
// vez y se usan TANTO en la carga inicial como en "Limpiar", para que ambas
// rutas coincidan con lo que muestran los controles en pantalla.
const FILTROS_POR_DEFECTO = buildFilters(AUDITORIA_FILTROS_EMPTY);

function AuditoriaClinicaIndex() {
    const [draft, setDraft] = useState(AUDITORIA_FILTROS_EMPTY);
    const [applied, setApplied] = useState(FILTROS_POR_DEFECTO);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState(null);

    const [detalleOpen, setDetalleOpen] = useState(false);
    const [idcitaSeleccionada, setIdcitaSeleccionada] = useState(null);

    const fetchData = useCallback(async (p = 1, ps = 20, filters = {}) => {
        setLoading(true);
        setErrorMessage(null);
        const res = await GetAuditoriaClinicaCitas({ page: p, pageSize: ps, filters });
        if (res?.success) {
            setRows((res.data || []).map((r) => ({ ...r, key: r.idcita })));
            setTotal(res.pagination?.total || 0);
            setPage(res.pagination?.page || 1);
            setPageSize(res.pagination?.pageSize || ps);
        } else {
            setRows([]);
            setTotal(0);
            // RequirePermission responde 403 con este mismo contrato
            // {success:false, message}; 500 tambien cae aqui.
            setErrorMessage(res?.message || "Ocurrió un error al obtener el listado de auditoría clínica.");
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData(1, pageSize, FILTROS_POR_DEFECTO);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buscar = () => {
        const f = buildFilters(draft);
        setApplied(f);
        fetchData(1, pageSize, f);
    };
    const limpiar = () => {
        // "Limpiar" vuelve a los filtros por defecto (no a "sin filtro"): deja
        // los controles y el listado mostrando lo mismo.
        setDraft(AUDITORIA_FILTROS_EMPTY);
        setApplied(FILTROS_POR_DEFECTO);
        fetchData(1, pageSize, FILTROS_POR_DEFECTO);
    };

    const verAuditoria = (idcita) => {
        setIdcitaSeleccionada(idcita);
        setDetalleOpen(true);
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Row className="mb-2">
                    <Col>
                        <ol className="breadcrumb m-0">
                            <li className="breadcrumb-item">Auditoría</li>
                            <li className="breadcrumb-item active">Auditoría Clínica</li>
                        </ol>
                    </Col>
                </Row>

                <AuditoriaClinicaFiltros draft={draft} setDraft={setDraft} onBuscar={buscar} onLimpiar={limpiar} />

                {errorMessage && (
                    <Alert type="error" showIcon message={errorMessage} className="mb-3" data-testid="auditoria-clinica-error" />
                )}

                <AuditoriaClinicaTabla
                    rows={rows}
                    loading={loading}
                    page={page}
                    pageSize={pageSize}
                    total={total}
                    onChange={(pg) => fetchData(pg.current, pg.pageSize, applied)}
                    onVerAuditoria={verAuditoria}
                />

                <AuditoriaClinicaDetalleDrawer
                    open={detalleOpen}
                    idcita={idcitaSeleccionada}
                    onClose={() => setDetalleOpen(false)}
                    onCorrectionSaved={() => fetchData(page, pageSize, applied)}
                />
            </div>
        </div>
    );
}

export default AuditoriaClinicaIndex;
